import { useEffect, useRef, useState } from 'react'
import type { OutputKind } from '@/api/types'
import { useAssetLibrary } from '@/context/AssetLibraryContext'
import { useToast } from '@/context/ToastContext'
import { toRelUrl } from '@/lib/mediaUrl'
import { Icon } from './icons'
import { SmartImage } from './SmartImage'
import { Badge, Button, Drawer, Dropzone, Empty } from './ui'

/**
 * 音频素材的格子。
 *
 * ## 为什么不能交给 `SmartImage`
 *
 * 它只认「图片 / 视频」两种：音频会掉进图片那一支，渲染成
 * `<img src="xxx.mp3">` —— 一张碎图。这与当年 `<img src="x.mp4">` 是同一个坑，
 * 也与「音频产出被 `ResultGrid` 静默滤掉」同源：**形态和渲染器没对上**。
 *
 * ## 为什么是一个自绘播放键 + 隐藏的 `<audio>`
 *
 * 原生 `<audio controls>` 有它自己的接口：上传后的音频要靠这个格子被选中，
 * 而原生播放器的**进度条就是最大的可点区域（在拖拽跳转）** —— 点它选不中素材。
 * 自绘一颗 32px 的播放键，点它出声、点格子其他地方选中，两件事不再抢同一块像素。
 * 播放状态只从 `<audio>` 的真事件回流（同 `AudioPlayerBar` 的口径：
 * 按钮的「在播」图标不许由点击乐观决定）。
 */
function AudioAssetMedia({ url, name }: { url: string; name: string }) {
  const ref = useRef<HTMLAudioElement | null>(null)
  const [playing, setPlaying] = useState(false)
  const [err, setErr] = useState<string | null>(null)

  const toggle = () => {
    const el = ref.current
    if (!el) return
    if (el.paused) {
      setErr(null)
      void el.play().catch((e: unknown) => {
        // `AbortError` = 上一次 play() 被 pause() 打断，不是故障（见 AudioPlayerBar）
        if (e instanceof DOMException && e.name === 'AbortError') return
        setErr(e instanceof Error ? e.message.replace(/\s*https?:\/\/\S+\s*$/, '') : '播放失败')
      })
    } else {
      el.pause()
    }
  }

  return (
    <div className="asset__audio">
      <Icon name="wave" size={26} />
      <button
        type="button"
        className="asset__audioPlay"
        data-audio-asset-play="1"
        title="试听这段配音"
        aria-label={`试听 ${name}`}
        onClick={(e) => {
          // 卡片本身是「选中」的热区，播放键必须自己吃掉这一下，
          // 否则「想听一下」会顺手把它选成参考素材。
          e.stopPropagation()
          toggle()
        }}
      >
        <Icon name={playing ? 'pause' : 'play'} size={14} />
      </button>
      {err ? (
        <span className="asset__audioErr" title={err}>
          {err}
        </span>
      ) : null}
      <audio
        ref={ref}
        src={toRelUrl(url)}
        preload="metadata"
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        onEnded={() => setPlaying(false)}
        onError={() => setErr('音频加载失败')}
        style={{ display: 'none' }}
      />
    </div>
  )
}

/**
 * 素材库抽屉。
 *
 * **不再接收 `open` / `onClose` 两个 prop** —— 开关状态统一由 AssetLibraryContext 管。
 * 旧写法里 open 在 AppShell、picking 在 Context，两处互不相干，
 * 结果就是「从素材库选图」点了没反应（见 Context 里的注释）。
 * 收进同一个地方之后，选择模式与抽屉可见性天然一致。
 *
 * 选择模式分两种：`single` 点一下就收工，`many` 点选累积、底部按钮确认。
 * 多选模式下 `.asset--pickable` 的「点击即选」样式会误导（点了不关抽屉），
 * 所以选中态用独立的 `.asset--selected` + 勾选角标来表达。
 */
export function AssetLibraryDrawer() {
  const {
    assets,
    upload,
    uploading,
    remove,
    drawerOpen,
    closeDrawer,
    pickMode,
    pickLimit,
    pickKind,
    resolvePick,
    resolvePicks,
  } = useAssetLibrary()
  const [filter, setFilter] = useState<'all' | OutputKind>('all')
  const [selected, setSelected] = useState<string[]>([])

  /**
   * 真正生效的筛选。
   *
   * `pickKind` 优先：调用方说「本次只能选配音」时，那个限制**不许被抽屉里的
   * 筛选条推翻** —— 否则用户点一下「全部」就能挑一张图回去当配音，
   * 而约束写在调用方、界面却把它放开了。这一条是「约束落在控件里」的另一半：
   * 控件自己也不能提供绕过它的入口。
   */
  const active = pickKind ?? filter
  const toast = useToast()

  const picking = pickMode !== 'none'
  const multi = pickMode === 'many'

  // 每次进入选择模式都从零开始，否则上一次选剩的会带进来
  useEffect(() => {
    if (pickMode === 'none') setSelected([])
  }, [pickMode])

  const toggle = (id: string) => {
    setSelected((cur) => {
      if (cur.includes(id)) return cur.filter((x) => x !== id)
      if (pickLimit > 0 && cur.length >= pickLimit) {
        toast.error(`最多选 ${pickLimit} 个`)
        return cur
      }
      return [...cur, id]
    })
  }

  const confirm = () => {
    const items = selected
      .map((id) => assets.find((a) => a.id === id))
      .filter((a): a is NonNullable<typeof a> => Boolean(a))
    if (!items.length) {
      toast.error('还没有选中素材')
      return
    }
    resolvePicks(items)
    toast.success(`已选择 ${items.length} 个素材`)
  }

  const list = assets.filter((a) => (active === 'all' ? true : a.kind === active))

  /**
   * 筛选条。
   *
   * ⚠️ **音频是第四档**（2026-10-09 加）。加之前，语音合成产出的 mp3 也会被
   * `addGenerated` 登记进来，但它们既不是图片也不是视频 —— 三档筛选里
   * 只有「全部」能看到它们，而那一格里渲染的是 `SmartImage`（一张碎图）。
   * 现在它有自己的档位与自己的格子（见 `AudioAssetMedia`）。
   *
   * 被 `pickKind` 限定时这几颗键**整体不出现**：留一颗点不动的灰键，
   * 用户只会以为筛选坏了。
   */
  const FILTERS: { id: 'all' | OutputKind; label: string }[] = [
    { id: 'all', label: '全部' },
    { id: 'image', label: '图片' },
    { id: 'video', label: '视频' },
    { id: 'audio', label: '音频' },
  ]

  return (
    <Drawer
      open={drawerOpen}
      onClose={closeDrawer}
      title={picking ? '选择素材' : '素材库'}
      icon="archive"
      width={560}
      footer={
        <>
          <div className="row grow" style={{ gap: 6 }}>
            {/* 被 `pickKind` 限定时不给筛选条：那时只剩一种形态可选，
                摆一排点了会被忽略的键只会让人以为筛选坏了 */}
            {pickKind ? (
              <span className="chip" data-active="true" data-locked-kind={pickKind}>
                只看{pickKind === 'audio' ? '音频' : pickKind === 'video' ? '视频' : '图片'}
              </span>
            ) : (
              FILTERS.map((f) => (
                <button
                  key={f.id}
                  className="chip"
                  data-active={filter === f.id}
                  type="button"
                  onClick={() => setFilter(f.id)}
                >
                  {f.label}
                </button>
              ))
            )}
          </div>
          {multi ? (
            <>
              <Button variant="outline" onClick={() => resolvePicks([])}>
                取消
              </Button>
              <Button variant="primary" icon="check" disabled={!selected.length} onClick={confirm}>
                加入已选 {selected.length ? `(${selected.length})` : ''}
              </Button>
            </>
          ) : picking ? (
            <Button variant="outline" onClick={() => resolvePick(null)}>
              取消
            </Button>
          ) : null}
        </>
      }
    >
      <div className="stack">
        {picking ? (
          <div className="text-xs muted" style={{ padding: '8px 10px', background: 'hsl(var(--primary) / .08)', borderRadius: 10 }}>
            {multi
              ? `点击素材加入本次选择${pickLimit > 0 ? `（最多 ${pickLimit} 个）` : ''}，选完点右下角确认。`
              : pickKind === 'audio'
                ? '点击任意一条配音，作为本次对口型用的音频。'
                : '点击任意素材作为本次生成的参考图。'}
          </div>
        ) : null}

        <Dropzone
          label="拖拽或点击上传"
          hint="支持图片 / 视频，可多选 · 全站共用同一素材库"
          multiple
          onFiles={(files) => void upload(files)}
        />

        {uploading ? (
          <div className="row text-sm muted">
            <span className="spinner" /> 正在上传…
          </div>
        ) : null}

        {!list.length ? (
          <Empty
            icon="archive"
            title="素材库是空的"
            text="上传过的参考图和生成结果都可以存进这里，之后在任意工作台一键复用。"
          />
        ) : (
          <div className="asset-grid">
            {list.map((a) => {
              const on = selected.includes(a.id)
              const selectable = picking
              return (
                <div
                  key={a.id}
                  className={`asset${selectable ? ' asset--pickable' : ''}${on ? ' asset--selected' : ''}`}
                  onClick={() => {
                    if (!selectable) return
                    if (multi) {
                      toggle(a.id)
                    } else {
                      resolvePick(a)
                      toast.success('已选择素材')
                    }
                  }}
                >
                  <div className="asset__media">
                    {/* 音频有自己的格子：`SmartImage` 只认图片/视频，
                        音频会渲染成一张碎图（`<img src="x.mp3">`）。 */}
                    {a.kind === 'audio' ? (
                      <AudioAssetMedia url={a.url} name={a.name} />
                    ) : (
                      <SmartImage src={a.thumbUrl || a.url} alt={a.name} kind={a.kind} />
                    )}
                    {on ? (
                      <span className="asset__check">
                        <Icon name="check" size={12} />
                      </span>
                    ) : null}
                  </div>
                  <div className="asset__meta">
                    <div className="asset__name" title={a.name}>
                      {a.name}
                    </div>
                    <div className="row" style={{ gap: 6 }}>
                      <Badge tone={a.source === 'generated' ? 'primary' : 'neutral'}>{a.source === 'generated' ? '生成' : '上传'}</Badge>
                      <div className="grow" />
                      {!picking ? (
                        <>
                          <button
                            className="asset__act"
                            title="复制地址"
                            onClick={(e) => {
                              e.stopPropagation()
                              void navigator.clipboard.writeText(a.url)
                              toast.success('地址已复制')
                            }}
                          >
                            <Icon name="link" size={12} />
                          </button>
                          <button
                            className="asset__act asset__act--danger"
                            title="移除"
                            onClick={(e) => {
                              e.stopPropagation()
                              remove(a.id)
                            }}
                          >
                            <Icon name="trash" size={12} />
                          </button>
                        </>
                      ) : null}
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </div>
    </Drawer>
  )
}
